// The teams panel's dropdown: a custom trigger + listbox, never a native
// `<select>`, and PORTALLED so it cannot be clipped.
//
// WHY NOT A NATIVE <select>. macOS renders the OS's own opaque grey menu in the
// system font, which inside a dark settings modal reads as a browser artifact
// rather than part of the product. `appearance-none` does not help: it styles
// the CLOSED control only. `RoleSelect` was written to get the last native
// select out of this panel; its behaviour lives here so the next control cannot
// drift back to one.
//
// WHY FlyoutPortal RATHER THAN AN ABSOLUTE DIV. An absolutely positioned menu
// escapes layout flow but NOT an ancestor's `overflow`. The allowance editor
// sits at the bottom of a member card, so its menu was cut off mid-option --
// visible for the role picker higher up the row, fatal here. FlyoutPortal
// renders into `document.body`, and additionally flips above/below and clamps
// to the viewport, which an absolute div cannot do at all.
//
// `closeOnCanvasViewportChange={false}` is REQUIRED outside the canvas: that
// option mounts `CanvasViewportDismissal`, which calls ReactFlow's
// `useOnViewportChange` and throws without a ReactFlow provider. The settings
// modal has none.
//
// Generic over the option value so a caller keeps its own union type
// (`TeamRole`, `AllowanceLimitType`, `AllowancePeriod`) rather than widening to
// string and casting back -- `apps/console` forbids `as`.

import { useEffect, useRef, useState } from 'react'

import { FlyoutPortal } from '../canvas-v2/nodes/shared/FlyoutPortal'
import { cn } from '../../lib/utils'

export default function ListboxSelect<T extends string>({
  ariaLabel,
  value,
  options,
  disabled = false,
  labelFor,
  onSelect,
  triggerTestId,
  menuTestId,
  optionTestId,
  /** Tailwind width/spacing for the trigger, so callers can size in context. */
  triggerClassName,
}: {
  ariaLabel: string
  value: T
  options: readonly T[]
  disabled?: boolean
  labelFor: (value: T) => string
  onSelect: (value: T) => void
  triggerTestId: string
  menuTestId: string
  optionTestId: (value: T) => string
  triggerClassName?: string
}) {
  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  // Close if the control re-renders into a disabled (in-flight) state --
  // otherwise the menu hangs open over a trigger that no longer responds.
  useEffect(() => {
    if (disabled) setOpen(false)
  }, [disabled])

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        data-testid={triggerTestId}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg px-2',
          'border border-white/15 bg-white/[0.08] text-xs font-medium text-white',
          'transition-colors hover:bg-white/[0.14]',
          'focus:outline-none focus:ring-2 focus:ring-white/30',
          'disabled:cursor-not-allowed disabled:opacity-50',
          triggerClassName,
        )}
      >
        <span className="truncate">{labelFor(value)}</span>
        <svg
          className={cn('h-2.5 w-2.5 shrink-0 transition-transform', open && 'rotate-180')}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <FlyoutPortal
        triggerRef={triggerRef}
        isOpen={open}
        onClose={() => setOpen(false)}
        onEscape={() => setOpen(false)}
        closeOnCanvasViewportChange={false}
        // FlyoutPortal defaults to "above" because its canvas callers are
        // config pills pinned near the bottom of the screen. A dropdown in a
        // settings form should open downward like any other; it still flips up
        // automatically when there is no room below.
        preferredPlacement="below"
        // ABOVE ui/Modal's z-[1100]. The portal mounts into document.body, so
        // it is a sibling of the settings modal rather than a descendant and
        // does not inherit its stacking -- at the canvas default of 150 the
        // menu rendered behind the modal and was invisible while still being
        // in the DOM, which is exactly what a portal-presence test cannot see.
        zIndex={1200}
        minWidth={160}
        testId={menuTestId}
        // The default surface is a canvas config panel: 70vh, scrollable, p-3.
        // A three-option listbox wants none of that.
        surfaceClassName="rounded-lg border border-border bg-surface-1 py-1 shadow-xl"
      >
        <div role="listbox" aria-label={ariaLabel}>
          {options.map((option) => (
            <button
              key={option}
              type="button"
              role="option"
              aria-selected={option === value}
              data-testid={optionTestId(option)}
              onClick={() => {
                setOpen(false)
                onSelect(option)
              }}
              className={cn(
                'flex w-full items-center whitespace-nowrap px-3 py-1.5 text-left text-xs font-medium',
                'transition-colors text-text-secondary hover:bg-surface-2 hover:text-foreground',
              )}
            >
              {labelFor(option)}
            </button>
          ))}
        </div>
      </FlyoutPortal>
    </>
  )
}
